<script setup lang="ts">
/**
 * Fiat-transactions directory container.
 *
 * Owns:
 *   - filter state + URL sync (via `useFiatTransactionsListFilters`)
 *   - list query (server-side pagination + filter)
 *   - download CSV flow (via `useFiatTransactionsDownload`)
 *   - treasury accounts query for the inline quick filter
 *
 * The treasury account is the lens the operator already filters by
 * inline; every other filter (date range, investor name/email,
 * reference type, direction, reason, amount min/max, sort order)
 * lives in the side filter drawer.
 *
 * Row click navigates to the dedicated detail page — directory pages
 * use a list + detail page pattern, NOT list + side drawer.
 */
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import { routeNames } from '@app/router/routes'

import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Message from '@uiKit/elements/Message.vue'

import FiatTransactionsFilterDrawer from '../components/FiatTransactionsFilterDrawer.vue'
import FiatTransactionsList from '../components/FiatTransactionsList.vue'

import { useFiatTransactionsListQuery } from '../api/queries/useFiatTransactionsListQuery'
import { useFiatTransactionsDownload } from '../composables/useFiatTransactionsDownload'
import { useFiatTransactionsListFilters } from '../composables/useFiatTransactionsListFilters'
import { useTreasuryBankAccountsQuery } from '../../treasuryAccounts'

import type { FiatTransactionRow } from '../types/fiatTransactionsTypes'

// ─── Filters + pagination (server-side) ─────────────────────────────────────

const {
  currentPage,
  pageSize,
  drawerFilters,
  quickFilters,
  listParams,
  activeFilterCount,
  hasActiveFilters,
  applyDrawerFilters,
  setTreasuryAccountId,
  resetFilters,
} = useFiatTransactionsListFilters()

const filterDrawerVisible = ref(false)

// ─── List query ─────────────────────────────────────────────────────────────

const {
  data: listData,
  isLoading: listLoading,
  isFetching: listFetching,
  error: listError,
  refetch: refetchList,
} = useFiatTransactionsListQuery(listParams)

const rows = computed<FiatTransactionRow[]>(() => listData.value?.data ?? [])
const totalRows = computed(() => listData.value?.meta.total ?? 0)

// Treasury accounts feed the inline quick filter Select.
const { data: treasuryAccountsData, isLoading: treasuryAccountsLoading } =
  useTreasuryBankAccountsQuery()
const treasuryAccounts = computed(() => treasuryAccountsData.value ?? [])

const emptyTitle = computed(() =>
  hasActiveFilters.value ? 'No transactions match your filters' : 'No transactions yet',
)
const emptySubtitle = computed(() =>
  hasActiveFilters.value
    ? 'Adjust filters to widen the search.'
    : 'New ledger transactions will show up here as investors deposit and withdraw.',
)

function describeError(err: unknown): string {
  if (!err) return ''
  if (err instanceof Error) return err.message
  const candidate = err as { message?: unknown; code?: unknown }
  if (typeof candidate.message === 'string') return candidate.message
  if (typeof candidate.code === 'string') return candidate.code
  return 'Something went wrong. Please try again.'
}

function openFilterDrawer() {
  filterDrawerVisible.value = true
}

// ─── Row click → detail page ────────────────────────────────────────────────

const router = useRouter()

function handleRowClick(row: FiatTransactionRow) {
  router.push({ name: routeNames.TREASURY_FIAT_TRANSACTIONS_DETAIL, params: { id: row.id } })
}

// ─── CSV download ───────────────────────────────────────────────────────────

const { isDownloading, handleDownload } = useFiatTransactionsDownload(listParams)
</script>

<template>
  <Box flex direction="col" gap="4">
    <Box flex align="start" justify="between" gap="3" class="page-header">
      <Box flex direction="col" gap="1">
        <Text type="page-title">All transactions</Text>
        <Text type="body-sm" color="muted"> {{ totalRows.toLocaleString() }} transactions </Text>
      </Box>
      <Button
        label="Download CSV"
        icon="download"
        variant="outline"
        :loading="isDownloading"
        @click="handleDownload"
      />
    </Box>

    <Message v-if="listError" color="danger" variant="outlined" size="small">
      <Box flex justify="between" align="center" gap="3">
        <Text type="body-sm">Could not load transactions. {{ describeError(listError) }}</Text>
        <Button label="Try again" variant="text" color="danger" @click="refetchList()" />
      </Box>
    </Message>

    <FiatTransactionsList
      :rows="rows"
      :loading="listLoading || listFetching"
      :total="totalRows"
      :page="currentPage"
      :page-size="pageSize"
      :filter-count="activeFilterCount"
      :empty-title="emptyTitle"
      :empty-subtitle="emptySubtitle"
      :treasury-accounts="treasuryAccounts"
      :treasury-accounts-loading="treasuryAccountsLoading"
      :treasury-account-id="quickFilters.treasuryAccountId"
      @update:page="currentPage = $event"
      @update:page-size="pageSize = $event"
      @update:treasury-account-id="setTreasuryAccountId"
      @filter-click="openFilterDrawer"
      @row-click="handleRowClick"
    />
  </Box>

  <FiatTransactionsFilterDrawer
    :visible="filterDrawerVisible"
    :values="drawerFilters"
    @update:visible="filterDrawerVisible = $event"
    @apply="applyDrawerFilters"
    @reset="resetFilters"
  />
</template>

<style scoped>
.page-header {
  /* keep header aligned to top so a multi-line subtitle stays clean */
}
</style>
